import { useDefinedParams } from '@/hooks/useDefinedParams';
import * as Yup from 'yup';
import { JobDetails, setJobDetails } from '@/reducers/jbpGeneralSlice';
import { FormTextArea } from '@portal_components/components/FormTextArea';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { useAppDispatch, useAppSelector } from '@/store';
import { IndicatorType } from '@portal_components/components/WorkflowIndicator';
import { useNavigate } from 'react-router';
import { useUserInfo } from '@/hooks/useUserInfo';
import { FormWorkflowView } from '@portal_components/formWorkflow/FormWorkflowView';
import { Title } from '@portal_components/components/Title';
import { jobCommentsNode, siteAddressNode } from '../GenericNodes';

export const FormBookJobWarrantInfo = () => {
  const [mpan, serviceLine, jobTypeCode] = useDefinedParams(['mpan', 'serviceLine', 'jobTypeCode']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { site } = useAppSelector(state => state.jbp);
  const { isInternalUser } = useUserInfo();

  const handleSubmit = (values: JobDetails) => {
    dispatch(setJobDetails({ ...values, jobTypeCode, isWarrantJob: true }));

    if (isInternalUser) {
      navigate(`/bookJob/InternalOffers/${mpan}/${serviceLine}`);
    } else {
      navigate(`/bookJob/offers/${mpan}/${serviceLine}`);
    }
  };

  if (!site || !site.siteContactDetails || !site.siteAddress) return null;
  return (
    <div className="ml-10 pb-4 mb-10">
      <Title className="mt-7 mb-5" text={`Booking for: ${mpan}`} />
      <FormWorkflowView<JobDetails>
        onSubmit={handleSubmit}
        initialValues={{
          siteContactDetails: site.siteContactDetails,
          siteAddress: site.siteAddress
        }}
        nodes={[
          {
            element: (
              <div>
                <Title text="Please provide contact details for the warrant officer who will be attending" className="mb-5 mt-7" />
                <div className="w-[30em]">
                  <FormTextInput name="warrantOfficerName" title="Warrant Officer Name" placeholder="Please enter the full name of the warrant officer" mandatory />
                  <FormTextInput name="warrantOfficerContactNumber" title="Warrant Officer Contact Number" placeholder="Please enter a contact number for the warrant officer" mandatory />
                </div>
              </div>
            ),
            validationSchema: {
              warrantOfficerName: Yup.string().required("Please enter the warrant officer's name").max(50, 'Warrant officer name cannot exceed 50 characters'),
              warrantOfficerContactNumber: Yup.string()
                .required("Please enter the warrant officer's contact number")
                .matches(/^0[12378]\d{8}\d?$/, 'Please check the contact number is valid, ensuring no spaces')
            }
          },
          siteAddressNode,
          {
            name: 'whatThreeWords',
            element: (
              <div>
                <Title text="If known, please provide the What 3 Words location information" className="mb-5 mt-7" />
                <FormTextArea
                  name="whatThreeWords"
                  title="What 3 Words Location Information"
                  placeholder="What 3 Words reference in the following format: wordone.wordtwo.wordthree"
                  className="w-[30em]"
                />
              </div>
            ),
            indicatorType: IndicatorType.Default,
            validationSchema: { whatThreeWords: Yup.string().optional() }
          },
          jobCommentsNode
        ]}
      />
    </div>
  );
};
